import type {
  AgentEffort,
  AgentProfile,
  CreateAgentProfileRequest,
  UpdateAgentProfileRequest,
} from '@simple-agent-manager/shared';
import { DEFAULT_AGENT_EFFORT } from '@simple-agent-manager/shared';
import { Button, Spinner } from '@simple-agent-manager/ui';
import { Bot, Pencil, Plus, Trash2 } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ProfileFormDialog } from './ProfileFormDialog';

interface ProfileListProps {
  profiles: AgentProfile[];
  loading: boolean;
  error: string | null;
  onCreateProfile: (data: CreateAgentProfileRequest) => Promise<AgentProfile>;
  onUpdateProfile: (profileId: string, data: UpdateAgentProfileRequest) => Promise<AgentProfile>;
  onDeleteProfile: (profileId: string) => Promise<void>;
  /** Hide the built-in header when the parent provides its own heading */
  hideHeader?: boolean;
  /** Project ID — needed for profile runtime asset management */
  projectId: string;
}

function formatGitHubCliPolicySummary(profile: AgentProfile): string | null {
  const policy = profile.githubCliPolicy;
  if (policy?.mode !== 'custom') return null;

  const denied = Object.entries(policy.permissions)
    .filter(([, level]) => level === 'none')
    .map(([name]) => name);
  const readableNames: Record<string, string> = {
    contents: 'code',
    pullRequests: 'PRs',
    issues: 'issues',
    actions: 'actions',
    packages: 'packages',
  };

  if (denied.length > 0) {
    return `GitHub CLI: no ${denied.map((name) => readableNames[name] ?? name).join(', ')}`;
  }

  return 'GitHub CLI: repository scoped';
}

const EFFORT_LABELS: Record<AgentEffort, string> = {
  auto: 'auto',
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'xhigh',
  max: 'max',
};

export const ProfileList: FC<ProfileListProps> = ({
  profiles,
  loading,
  error,
  onCreateProfile,
  onUpdateProfile,
  onDeleteProfile,
  hideHeader,
  projectId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // URL-driven edit modal — `?edit=profileId` or `?edit=new`
  const editParam = searchParams.get('edit');
  const formOpen = editParam !== null;
  const editingProfile = useMemo(
    () =>
      editParam && editParam !== 'new' ? (profiles.find((p) => p.id === editParam) ?? null) : null,
    [editParam, profiles]
  );

  const openForm = useCallback(
    (profileId?: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('edit', profileId ?? 'new');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const closeForm = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleCreate = () => {
    openForm();
  };

  const handleEdit = (profile: AgentProfile) => {
    openForm(profile.id);
  };

  const handleSave = async (data: CreateAgentProfileRequest | UpdateAgentProfileRequest) => {
    if (editingProfile) {
      await onUpdateProfile(editingProfile.id, data as UpdateAgentProfileRequest);
    } else {
      await onCreateProfile(data as CreateAgentProfileRequest);
    }
  };

  const handleDelete = async (profileId: string) => {
    setDeleteError(null);
    try {
      await onDeleteProfile(profileId);
      setDeleteConfirmId(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete profile');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <div className="py-4 px-3 rounded-sm bg-danger-tint text-danger text-sm">{error}</div>;
  }

  return (
    <div>
      {!hideHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-fg-primary">Agent Profiles</h3>
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-1.5 inline-block" />
            New Profile
          </Button>
        </div>
      )}
      {hideHeader && (
        <div className="flex justify-end mb-4">
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-1.5 inline-block" />
            New Profile
          </Button>
        </div>
      )}

      {profiles.length === 0 ? (
        <div className="text-center py-8 text-fg-muted text-sm">
          <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No profiles yet. Create one to bundle agent and infrastructure settings.
        </div>
      ) : (
        <div className="grid gap-2">
          {profiles.map((profile) => (
            <div key={profile.id} className="overflow-hidden rounded-md glass-surface">
              <div className="flex items-start gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-fg-primary truncate">
                      {profile.name}
                    </span>
                    {profile.isBuiltin && (
                      <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.15)] text-accent">
                        built-in
                      </span>
                    )}
                  </div>
                  {profile.description && (
                    <p className="text-xs text-fg-muted mt-0.5 line-clamp-2">
                      {profile.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-fg-muted">
                    <span>{profile.agentType}</span>
                    {profile.model && <span>{profile.model}</span>}
                    {profile.effort !== DEFAULT_AGENT_EFFORT && (
                      <span>Effort: {EFFORT_LABELS[profile.effort]}</span>
                    )}
                    {profile.permissionMode && <span>{profile.permissionMode}</span>}
                    {profile.vmSizeOverride && <span>VM: {profile.vmSizeOverride}</span>}
                    {profile.taskMode && <span>Mode: {profile.taskMode}</span>}
                    {formatGitHubCliPolicySummary(profile) && (
                      <span>{formatGitHubCliPolicySummary(profile)}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(profile)}
                    aria-label={`Edit ${profile.name}`}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded text-fg-muted hover:text-fg-primary hover:bg-surface cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus-ring"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {deleteConfirmId !== profile.id && (
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteConfirmId(profile.id);
                        setDeleteError(null);
                      }}
                      aria-label={`Delete ${profile.name}`}
                      className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded text-fg-muted hover:text-danger hover:bg-danger-tint cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus-ring"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {deleteConfirmId === profile.id && (
                <div className="flex items-center justify-end gap-2 px-3 pb-3">
                  <span className="text-xs text-fg-muted mr-auto">Delete this profile?</span>
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteConfirmId(null);
                      setDeleteError(null);
                    }}
                    aria-label="Cancel delete"
                    className="px-3 py-2 min-w-[44px] min-h-[44px] rounded text-xs text-fg-muted hover:text-fg-primary cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus-ring"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(profile.id)}
                    aria-label={`Confirm delete ${profile.name}`}
                    className="px-3 py-2 min-h-[44px] rounded text-xs text-danger bg-danger-tint hover:bg-danger hover:text-white cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus-ring"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {deleteError && (
        <div role="alert" className="mt-2 py-2 px-3 rounded-sm bg-danger-tint text-danger text-xs">
          {deleteError}
        </div>
      )}

      <ProfileFormDialog
        isOpen={formOpen}
        onClose={closeForm}
        profile={editingProfile ?? null}
        onSave={handleSave}
        projectId={projectId}
      />
    </div>
  );
};
